import React, { useCallback, useEffect, useRef, useState } from 'react'

import { format, isAfter, isBefore } from 'date-fns'
import { type DateRange, type DayPickerProps } from 'react-day-picker'

import { DateCalendar } from './DateCalendar'
import { IconCalendar, IconPointerRight } from '../Icon'
import { CalendarIconStyled } from './styles/CalendarIconStyled'
import { DatePickerInputStyled } from './styles/DatePickerInputStyled'
import { DatePickerPopoverStyled } from './styles/DatePickerPopoverStyled'
import { DateRangePickerStyled } from './styles/DateRangePickerStyled'
import type { DateRangePreset, DateValue, FocusedRangeInput } from './types'
import { parseDateInput } from './utils/parseDateInput'

export interface IDateRangePickerProps {
  startDate: DateValue
  endDate: DateValue
  /** Unique ID for the start date input */
  startDateId: string
  /** Unique ID for the end date input */
  endDateId: string
  /** Called when dates change */
  onDatesChange: (dates: { startDate: DateValue; endDate: DateValue }) => void
  /** Which input is focused */
  focusedInput: FocusedRangeInput
  onFocusChange: (focusedInput: FocusedRangeInput) => void
  startDatePlaceholderText?: string
  endDatePlaceholderText?: string
  /** Show error styling */
  error?: boolean
  /** Full-width display */
  block?: boolean
  /** Disabled state */
  disabled?: boolean
  /** Function to disable specific dates */
  isOutsideRange?: (day: Date) => boolean
  /** Keep popover open after selecting a date */
  keepOpenOnDateSelect?: boolean
  /** Array of preset date ranges */
  presets?: DateRangePreset[]
  /** Earliest selectable month in month/year dropdown */
  fromMonth?: Date
  /** Latest selectable month in month/year dropdown */
  toMonth?: Date
  /** Locale for formatting and calendar display */
  locale?: DayPickerProps['locale']
}

export const DateRangePicker = ({
  startDate,
  endDate,
  startDateId,
  endDateId,
  onDatesChange,
  focusedInput,
  onFocusChange,
  startDatePlaceholderText = 'Start date',
  endDatePlaceholderText = 'End date',
  error = false,
  block = false,
  disabled = false,
  isOutsideRange,
  keepOpenOnDateSelect = true,
  presets,
  fromMonth,
  toMonth,
  locale,
}: IDateRangePickerProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [month, setMonth] = useState<Date>(startDate ?? new Date())

  const [startInputValue, setStartInputValue] = useState<string>(
    startDate ? format(startDate, 'P') : ''
  )
  const [endInputValue, setEndInputValue] = useState<string>(
    endDate ? format(endDate, 'P') : ''
  )

  const startInputRef = useRef(null)

  useEffect(() => {
    if (startDate) {
      setMonth(startDate)
      setStartInputValue(format(startDate, 'P'))
    }
  }, [startDate])

  useEffect(() => {
    if (endDate) {
      setEndInputValue(format(endDate, 'P'))
    }
  }, [endDate])

  const handleClickOutside = useCallback(
    (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        if (focusedInput !== null) {
          if (!startDate || !endDate) {
            onDatesChange({ startDate: null, endDate: null })
          }
          onFocusChange(null)
        }
      }
    },
    [focusedInput, startDate, endDate, onDatesChange, onFocusChange]
  )

  const handleEscapeKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (!startDate || !endDate) {
          onDatesChange({ startDate: null, endDate: null })
        }
        onFocusChange(null)
      }
    },
    [startDate, endDate, onDatesChange, onFocusChange]
  )

  useEffect(() => {
    if (focusedInput) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscapeKey)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscapeKey)
    }
  }, [focusedInput, handleClickOutside, handleEscapeKey])

  const handleRangeSelect = (range: DateRange | undefined) => {
    const newStart = range?.from ?? null
    const newEnd = range?.to ?? null

    onDatesChange({ startDate: newStart, endDate: newEnd })

    if (newStart) {
      setStartInputValue(format(newStart, 'P'))
    }
    if (newEnd) {
      setEndInputValue(format(newEnd, 'P'))
    }

    if (newStart && newEnd && !keepOpenOnDateSelect) {
      onFocusChange(null)
    }
  }

  const handlePresetClick = (preset: { label: string }) => {
    const typedPreset = preset as DateRangePreset
    onDatesChange({ startDate: typedPreset.start, endDate: typedPreset.end })
    setStartInputValue(format(typedPreset.start, 'P'))
    setEndInputValue(format(typedPreset.end, 'P'))
    if (!keepOpenOnDateSelect) {
      onFocusChange(null)
    }
  }

  const handleStartFocus = () => {
    if (!disabled) {
      onFocusChange('startDate')
    }
  }

  const handleEndFocus = (e) => {
    e.stopPropagation()
    if (!disabled && focusedInput !== 'endDate') {
      onFocusChange('endDate')
    }
  }

  const handleInputClick = () => {
    if (!disabled) {
      startInputRef.current.focus()
    }
  }

  const handleStartInputChange = (e) => {
    setStartInputValue(e.target.value)
    const parsedDate = parseDateInput(e.target.value)
    if (parsedDate) {
      onDatesChange(
        isBefore(parsedDate, endDate)
          ? { startDate: parsedDate, endDate }
          : { startDate: endDate, endDate: parsedDate }
      )
      setMonth(parsedDate)
    }
  }

  const handleEndInputChange = (e) => {
    setEndInputValue(e.target.value)
    const parsedDate = parseDateInput(e.target.value)
    if (parsedDate) {
      onDatesChange(
        isAfter(parsedDate, startDate)
          ? { startDate, endDate: parsedDate }
          : { startDate: parsedDate, endDate: startDate }
      )
    }
  }

  const selected: DateRange | undefined =
    startDate || endDate
      ? { from: startDate ?? undefined, to: endDate ?? undefined }
      : undefined

  return (
    <DateRangePickerStyled ref={containerRef} block={block}>
      <DatePickerInputStyled
        error={error}
        disabled={disabled}
        focused={focusedInput !== null}
        block={block}
        onClick={handleInputClick}
      >
        <input
          ref={startInputRef}
          id={startDateId}
          disabled={disabled}
          placeholder={startDatePlaceholderText}
          value={startInputValue}
          onChange={handleStartInputChange}
          onFocus={handleStartFocus}
          aria-label={startDatePlaceholderText}
        />
        <IconPointerRight />
        <input
          id={endDateId}
          disabled={disabled}
          placeholder={endDatePlaceholderText}
          value={endInputValue}
          onChange={handleEndInputChange}
          onClick={handleEndFocus}
          onFocus={handleEndFocus}
          aria-label={endDatePlaceholderText}
        />
        <CalendarIconStyled>
          <IconCalendar color='textPrimary' ariaLabel='Open calendar' />
        </CalendarIconStyled>
      </DatePickerInputStyled>

      {focusedInput !== null && (
        <DatePickerPopoverStyled anchorDirection='left'>
          <DateCalendar
            mode='range'
            selected={selected}
            onSelect={handleRangeSelect}
            month={month}
            onMonthChange={setMonth}
            disabled={isOutsideRange}
            numberOfMonths={2}
            locale={locale}
            fromMonth={fromMonth}
            toMonth={toMonth}
            presets={presets}
            onPresetClick={handlePresetClick}
          />
        </DatePickerPopoverStyled>
      )}
    </DateRangePickerStyled>
  )
}

DateRangePicker.displayName = 'DateRangePicker'
